<template>
  <v-main>
    <v-container fluid>
      <v-row justify="center">
        <v-col cols="12" lg="10" xl="8">
          <!-- Loading State -->
          <v-card v-if="loading" elevation="3">
            <v-card-text class="text-center py-8">
              <health-loader message="Loading HSN Master Details..." />
            </v-card-text>
          </v-card>

          <!-- Main Content -->
          <div v-else-if="hsnMaster">
            <!-- Header Card -->
            <v-card elevation="3" class="mb-4">
              <v-card-title class="pb-2 pt-3 px-4">
                <v-row class="w-100 ma-0" align="center">
                  <v-col cols="12" class="pa-0">
                    <div class="d-flex align-center justify-between">
                      <div class="d-flex align-center">
                        <v-btn
                          icon
                          small
                          @click="$router.push('/hsn-masters')"
                          class="mr-2"
                        >
                          <v-icon>mdi-arrow-left</v-icon>
                        </v-btn>
                        <div>
                          <h2 class="text-h6 font-weight-bold primary--text mb-0">
                            HSN Master Details
                          </h2>
                          <p class="text-caption grey--text ma-0 mt-1">
                            View and manage HSN code information
                          </p>
                        </div>
                      </div>
                      
                      <div class="d-flex align-center">
                        <v-chip
                          :color="hsnMaster.is_active ? 'success' : 'error'"
                          text-color="white"
                          small
                          class="mr-2"
                        >
                          <v-icon left small>
                            {{ hsnMaster.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                          </v-icon>
                          {{ hsnMaster.is_active ? 'Active' : 'Inactive' }}
                        </v-chip>
                        
                        <v-btn
                          color="primary"
                          small
                          :to="`/hsn-masters/create?edit=${hsnMaster.encrypted_id}`"
                        >
                          <v-icon left small>mdi-pencil</v-icon>
                          Edit
                        </v-btn>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-title>
            </v-card>

            <!-- HSN Master Information -->
            <v-row>
              <v-col cols="12" md="8">
                <v-card elevation="3" class="mb-4">
                  <v-card-title>
                    <v-icon left color="primary">mdi-barcode-scan</v-icon>
                    HSN Master Information
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <!-- HSN Code -->
                      <v-col cols="12" md="6">
                        <div class="info-item">
                          <div class="info-label">HSN/SAC Code</div>
                          <div class="info-value">
                            <v-chip
                              color="primary"
                              text-color="white"
                              class="font-weight-bold"
                            >
                              {{ hsnMaster.hsn_code || 'N/A' }}
                            </v-chip>
                          </div>
                        </div>
                      </v-col>

                      <!-- GST Rate -->
                      <v-col cols="12" md="6">
                        <div class="info-item">
                          <div class="info-label">GST Rate</div>
                          <div class="info-value">
                            <v-chip
                              :color="getGSTRateColor(hsnMaster.gst_rate)"
                              text-color="white"
                              class="font-weight-bold"
                            >
                              <v-icon left small>mdi-percent</v-icon>
                              {{ hsnMaster.gst_rate !== null ? hsnMaster.gst_rate + '%' : 'N/A' }}
                            </v-chip>
                          </div>
                        </div>
                      </v-col>

                      <!-- Description -->
                      <v-col cols="12">
                        <div class="info-item">
                          <div class="info-label">Description</div>
                          <div class="info-value">
                            <p class="text-body-1 mb-0" style="line-height: 1.6;">
                              {{ hsnMaster.description || 'No description provided' }}
                            </p>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- System Information -->
              <v-col cols="12" md="4">
                <v-card elevation="3" class="mb-4">
                  <v-card-title>
                    <v-icon left color="info">mdi-information</v-icon>
                    System Information
                  </v-card-title>
                  <v-card-text>
                    <div class="info-item mb-3">
                      <div class="info-label">Status</div>
                      <div class="info-value">
                        <v-chip
                          :color="hsnMaster.is_active ? 'success' : 'error'"
                          text-color="white"
                          x-small
                        >
                          {{ hsnMaster.is_active ? 'Active' : 'Inactive' }}
                        </v-chip>
                      </div>
                    </div>

                    <div class="info-item mb-3">
                      <div class="info-label">Created Date</div>
                      <div class="info-value">
                        <v-icon small class="mr-1">mdi-calendar</v-icon>
                        {{ formatDateTime(hsnMaster.created_at) }}
                      </div>
                    </div>

                    <div class="info-item mb-3">
                      <div class="info-label">Last Updated</div>
                      <div class="info-value">
                        <v-icon small class="mr-1">mdi-update</v-icon>
                        {{ formatDateTime(hsnMaster.updated_at) }}
                      </div>
                    </div>

                    <div class="info-item mb-3" v-if="hsnMaster.created_by">
                      <div class="info-label">Created By</div>
                      <div class="info-value">
                        <v-icon small class="mr-1">mdi-account</v-icon>
                        {{ hsnMaster.created_by.first_name }} {{ hsnMaster.created_by.last_name }}
                      </div>
                    </div>

                    <div class="info-item">
                      <div class="info-label">Record ID</div>
                      <div class="info-value">
                        <code class="text-caption">{{ hsnMaster.id }}</code>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- GST Information Card -->
                <v-card elevation="3">
                  <v-card-title>
                    <v-icon left color="orange">mdi-percent</v-icon>
                    GST Information
                  </v-card-title>
                  <v-card-text>
                    <div class="info-item mb-3">
                      <div class="info-label">Rate Category</div>
                      <div class="info-value">
                        <v-chip
                          :color="getGSTCategoryColor(hsnMaster.gst_rate)"
                          text-color="white"
                          x-small
                        >
                          {{ getGSTCategory(hsnMaster.gst_rate) }}
                        </v-chip>
                      </div>
                    </div>

                    <div class="info-item mb-3">
                      <div class="info-label">Applicable Rate</div>
                      <div class="info-value font-weight-bold text-h6">
                        {{ hsnMaster.gst_rate !== null ? hsnMaster.gst_rate + '%' : 'Not Set' }}
                      </div>
                    </div>

                    <v-alert
                      :type="getGSTAlertType(hsnMaster.gst_rate)"
                      dense
                      text
                      class="mb-0"
                    >
                      {{ getGSTDescription(hsnMaster.gst_rate) }}
                    </v-alert>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Action Buttons -->
            <v-row class="mt-4">
              <v-col cols="12">
                <v-card elevation="3">
                  <v-card-title>Actions</v-card-title>
                  <v-card-text>
                    <div class="d-flex flex-wrap gap-2">
                      <v-btn
                        color="primary"
                        :to="`/hsn-masters/create?edit=${hsnMaster.encrypted_id}`"
                      >
                        <v-icon left>mdi-pencil</v-icon>
                        Edit Details
                      </v-btn>

                      <v-btn
                        color="info"
                        outlined
                        @click="copyHSNCode"
                      >
                        <v-icon left>mdi-content-copy</v-icon>
                        Copy HSN Code
                      </v-btn>

                      <v-btn
                        :color="hsnMaster.is_active ? 'warning' : 'success'"
                        outlined
                        @click="toggleStatus"
                        :loading="statusLoading"
                      >
                        <v-icon left>{{ hsnMaster.is_active ? 'mdi-pause' : 'mdi-play' }}</v-icon>
                        {{ hsnMaster.is_active ? 'Deactivate' : 'Activate' }}
                      </v-btn>

                      <v-btn
                        color="error"
                        outlined
                        @click="deleteItem"
                        :loading="deleteLoading"
                      >
                        <v-icon left>mdi-delete</v-icon>
                        Delete
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <!-- Error State -->
          <v-card v-else elevation="3">
            <v-card-text class="text-center py-8">
              <v-icon size="64" color="error" class="mb-4">mdi-alert-circle</v-icon>
              <h3 class="text-h6 error--text mb-2">HSN Master Not Found</h3>
              <p class="text-body-2 grey--text">
                The requested HSN Master could not be found or you don't have permission to view it.
              </p>
              <v-btn color="primary" @click="$router.push('/hsn-masters')">
                <v-icon left>mdi-arrow-left</v-icon>
                Back to HSN Masters
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Confirmation Dialog -->
      <delete-confirmation-dialog
        v-model="deleteDialog"
        :item-name="`HSN Code: ${hsnMaster?.hsn_code}`"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </v-container>
  </v-main>
</template>

<script>
import { mapState } from "vuex";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";
import axios from "@/axios";

export default {
  name: "HsnMasterDetail",
  components: {
    DeleteConfirmationDialog,
  },
  data() {
    return {
      loading: true,
      statusLoading: false,
      deleteLoading: false,
      deleteDialog: false,
      hsnMaster: null,
    };
  },

  computed: {
    ...mapState(["user"]),
  },

  async mounted() {
    await this.fetchHsnMaster();
  },

  methods: {
    async fetchHsnMaster() {
      this.loading = true;
      try {
        const response = await axios.get(`/hsn-masters/${this.$route.params.id}`);
        this.hsnMaster = response.data.data;
      } catch (error) {
        console.error("Error fetching HSN Master:", error);
        this.$toast.error("Failed to fetch HSN Master details");
      } finally {
        this.loading = false;
      }
    },

    async toggleStatus() {
      this.statusLoading = true;
      try {
        const updatedData = {
          ...this.hsnMaster,
          is_active: !this.hsnMaster.is_active,
        };
        
        await axios.patch(`/hsn-masters/${this.$route.params.id}`, updatedData);
        this.hsnMaster.is_active = !this.hsnMaster.is_active;
        this.$toast.success(`HSN Master ${this.hsnMaster.is_active ? 'activated' : 'deactivated'} successfully`);
      } catch (error) {
        console.error("Error updating status:", error);
        this.$toast.error("Failed to update HSN Master status");
      } finally {
        this.statusLoading = false;
      }
    },

    deleteItem() {
      this.deleteDialog = true;
    },

    async confirmDelete() {
      this.deleteLoading = true;
      try {
        await axios.post(`/hsn-masters/delete/${this.hsnMaster.encrypted_id}`);
        this.$toast.success("HSN Master deleted successfully");
        this.$router.push("/hsn-masters");
      } catch (error) {
        console.error("Error deleting HSN Master:", error);
        this.$toast.error("Failed to delete HSN Master");
      } finally {
        this.deleteLoading = false;
        this.deleteDialog = false;
      }
    },

    cancelDelete() {
      this.deleteDialog = false;
    },

    async copyHSNCode() {
      try {
        await navigator.clipboard.writeText(this.hsnMaster.hsn_code);
        this.$toast.success("HSN Code copied to clipboard");
      } catch (error) {
        console.error("Error copying to clipboard:", error);
        this.$toast.error("Failed to copy HSN Code");
      }
    },

    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleString("en-IN");
    },

    getGSTRateColor(rate) {
      if (rate === 0) return "grey";
      if (rate <= 5) return "green";
      if (rate <= 12) return "blue";
      if (rate <= 18) return "orange";
      if (rate <= 28) return "red";
      return "purple";
    },

    getGSTCategoryColor(rate) {
      return this.getGSTRateColor(rate);
    },

    getGSTCategory(rate) {
      if (rate === 0) return "Exempt";
      if (rate <= 5) return "Essential";
      if (rate <= 12) return "Moderate";
      if (rate <= 18) return "Standard";
      if (rate <= 28) return "Luxury";
      return "Special";
    },

    getGSTAlertType(rate) {
      if (rate === 0) return "info";
      if (rate <= 5) return "success";
      if (rate <= 18) return "warning";
      return "error";
    },

    getGSTDescription(rate) {
      if (rate === 0) return "This item is exempt from GST";
      if (rate <= 5) return "Essential goods and services with minimal tax";
      if (rate <= 12) return "Processed food items, computers, and similar goods";
      if (rate <= 18) return "Most goods and services fall under this category";
      if (rate <= 28) return "Luxury items with higher tax rate";
      return "Special category items";
    },
  },
};
</script>

<style scoped>
.info-item {
  margin-bottom: 1rem;
}

.info-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #666;
  margin-bottom: 0.25rem;
}

.info-value {
  font-size: 0.95rem;
  font-weight: 400;
  color: #333;
}

.gap-2 > * {
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}

code {
  background-color: #f5f5f5;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: monospace;
}
</style>